<template>
  <teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-50 flex items-center justify-center"
      @keydown.esc.prevent="handleClose"
    >
      <!-- Backdrop -->
      <div class="fixed inset-0 backdrop-blur-sm" style="background-color: var(--overlay-dark-heavy);" @click="handleBackdrop"></div>

      <!-- Dialog Panel -->
      <div
        class="relative rounded-xl overflow-hidden"
        :style="{ 
          width: normalizedWidth, 
          maxWidth: '95vw',
          backgroundColor: 'var(--rogue-warm-cream)',
          border: '1px solid var(--gradient-linen)',
          boxShadow: 'var(--shadow-2xl)'
        }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4" style="border-bottom: 1px solid var(--gradient-linen);">
          <h2 v-if="title" :id="titleId" class="text-lg font-semibold" style="color: var(--rogue-deep-forest);">
            {{ title }}
          </h2>
          <div class="ml-auto">
            <Button variant="ghost" size="sm" @click="handleClose" :title="t('common.actions.close', 'Close')">
              ✕
            </Button>
          </div>
        </div>

        <!-- Body -->
        <div class="p-6">
          <slot />
        </div>

        <!-- Footer -->
        <div v-if="$slots.footer" class="px-6 py-4" style="border-top: 1px solid var(--gradient-linen); background-color: var(--gradient-ivory);">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from './Button.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  width: {
    type: [String, Number],
    default: '600px'
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true
  },
  id: {
    type: String,
    default: 'dialog'
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const { t } = useI18n()

const normalizedWidth = computed(() => {
  if (typeof props.width === 'number') return `${props.width}px`
  if (/^\d+$/.test(String(props.width))) return `${props.width}px`
  return props.width
})

const titleId = computed(() => `${props.id}-title`)

function handleClose() {
  emit('update:modelValue', false)
  emit('close')
}

function handleBackdrop() {
  if (props.closeOnBackdrop) handleClose()
}
</script>

<style scoped>
/* Focus states following design system */
:focus-visible {
  outline: 2px solid var(--rogue-vibrant-green);
  outline-offset: 2px;
  background-color: var(--overlay-green-light);
}

/* Remove outline on mouse click */
:focus:not(:focus-visible) {
  outline: none;
}
</style>